import { Capacitor } from '@capacitor/core';
import { lastPosEvents } from './pos/sessions';

/** Läuft die Web-App in der nativen Hülle (Android/iOS)? Im Browser und als PWA immer `false`. */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Form des Kopplungs-Tokens (32 Byte, URL-sicher kodiert); alles andere ist kein Kassen-Code. */
const TOKEN_FORM = /^[A-Za-z0-9_-]{20,128}$/;

/** Adressen, unter denen BARSO erreichbar ist (Hauptadresse und Ausweichadresse der App) plus die aktuelle Adresse. */
const KNOWN_HOSTS = ['basaro.de', 'basar.straub-it.de'];

function knownHost(host: string): boolean {
  const own = typeof window === 'undefined' ? '' : window.location.host;
  return host === own || KNOWN_HOSTS.includes(host);
}

export type PairingCheck = { ok: true; target: string } | { ok: false; reason: 'form' | 'host' };

/**
 * Prüft den Text eines gescannten QR-Codes oder eines eingefügten Links. Nur eine Adresse mit dem Pfad
 * `/kasse-koppeln`, bekanntem Host und dem Token im **Fragment** (`#token=…`) gilt. Ergebnis ist der interne Pfad samt
 * Fragment, zu dem die App navigiert; das Token gelangt so nie in Pfad oder Query. Ein fremder Host ist ein eigener
 * Fehler (der QR gehört nicht zu BARSO), alles andere „Form“.
 */
export function checkPairing(text: string): PairingCheck {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return { ok: false, reason: 'form' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return { ok: false, reason: 'form' };
  if (url.pathname.replace(/\/$/, '') !== '/kasse-koppeln') return { ok: false, reason: 'form' };
  const token = new URLSearchParams(url.hash.replace(/^#/, '')).get('token');
  if (token === null || !TOKEN_FORM.test(token)) return { ok: false, reason: 'form' };
  if (!knownHost(url.host)) return { ok: false, reason: 'host' };
  return { ok: true, target: `/kasse-koppeln#token=${token}` };
}

/**
 * QR-Code „Kassenseite für ein anderes Gerät“ (Kassen-Übersicht, `/basare/<id>/kasse`, bekannter Host): Ziel ist die
 * Kasse dieses Basars mit Anmeldung. Liefert den internen Pfad oder `null`. Enthält kein Token.
 */
export function registerPageTarget(text: string): string | null {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!knownHost(url.host)) return null;
  const match = /^\/basare\/([A-Za-z0-9-]{1,64})\/kasse\/?$/.exec(url.pathname);
  return match === null ? null : `/basare/${match[1] ?? ''}/kasse`;
}

/** Wie `checkPairing`, nur das Ziel; `null` bei jedem Fehler. */
export function pairingTarget(text: string): string | null {
  const result = checkPairing(text);
  return result.ok ? result.target : null;
}

/**
 * Ziel eines App-Links (Deep Link oder Verknüpfung „Kasse“): nur die Kopplung und die Kasse selbst. Alles andere
 * ergibt `null` (die App bleibt dann, wo sie ist), damit ein fremder Link keine beliebige Seite öffnet. `/kasse`
 * wird gleich aufgelöst: Ein Gerät mit Schnellzugang hat kein Konto und käme über die Anmeldeprüfung nie zur Kasse.
 */
export function appLinkTarget(link: string): string | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (!knownHost(url.host)) return null;
  const path = url.pathname.replace(/\/$/, '');
  if (path === '/kasse') {
    const last = lastPosEvents()[0];
    return last?.device === true ? `/kasse-geraet/${last.eventId}` : '/kasse';
  }
  if (path === '/kasse-koppeln') return pairingTarget(link);
  return null;
}

/**
 * In der App reicht die Ansicht bis unter Notch und Gestenleiste (`viewport-fit=cover`, die Oberfläche hält mit
 * `env(safe-area-inset-*)` Abstand). Im Browser bleibt der Viewport unverändert.
 */
export function applyNativeViewport(): void {
  if (!isNativeApp()) return;
  const meta = document.querySelector('meta[name="viewport"]');
  const content = meta?.getAttribute('content') ?? '';
  if (meta !== null && !content.includes('viewport-fit')) {
    meta.setAttribute('content', `${content}, viewport-fit=cover`);
  }
}
